import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormControlStatus,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';

import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { noWhitespaceValidator, vatNumberRequiredValidator } from '@recruitee/common';
import type { ChargebeeEntity } from '@recruitee/company-types';
import { RegionsStoreService, injectCountryList } from '@recruitee/country-data';
import { DevFlag, injectDevFlag } from '@recruitee/dev-flags-data';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { Currency } from '@recruitee/pricing-types';
import {
  DialogService,
  SelectOptionValue,
  IconHelpWithDropdownComponent,
  SelectComponent,
  InputComponent,
  LabelComponent,
  FormFieldComponent,
} from '@recruitee/user-interface';

import { getCorrectVatPlaceholder } from '../../helpers/get-correct-vat-placeholder';
import { getCountryAvailableCurrencies } from '../../helpers/get-country-available-currencies';
import { BillingAddressFormFields, PricingFeatureFacade } from '../../types/pricing-feature.types';

const showVatField = [
  'AT',
  'BE',
  'BG',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GB',
  'GR',
  'HR',
  'HU',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
];

const countriesWithState = ['US'];

@Component({
  selector: 'rt-billing-address-form',
  templateUrl: './billing-address-form.component.html',
  styleUrls: ['./billing-address-form.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormFieldComponent,
    LabelComponent,
    InputComponent,
    SelectComponent,
    IconHelpWithDropdownComponent,
    TranslatePipe,
  ],
})
export class BillingAddressFormComponent implements OnInit {
  public form: UntypedFormGroup;
  public countries: Array<{ option: string; value: SelectOptionValue }> = injectCountryList().map(
    ({ name, code }) => ({ option: name, value: code }),
  );
  public regions: Array<{ option: string; value: SelectOptionValue }> = [];

  @Input() public formValue: BillingAddressFormFields;
  @Input() public isManual: boolean = false;
  @Input() public countriesLoaded: boolean = true;
  @Input() public selectedCurrency: Currency;
  @Input() public chargebeeEntity: ChargebeeEntity;

  @Output() public valueChanges = new EventEmitter<BillingAddressFormFields>();
  @Output() public statusChanges = new EventEmitter<FormControlStatus>();
  @Output() public currencyChanges = new EventEmitter<Currency>();

  private tellentSasEnabled = injectDevFlag(DevFlag.TellentSas);

  constructor(
    private fb: UntypedFormBuilder,
    private cdRef: ChangeDetectorRef,
    private facade: PricingFeatureFacade,
    private dialogSvc: DialogService,
    private i18n: I18nService,
    private regionsStoreService: RegionsStoreService,
  ) {}

  public ngOnInit(): void {
    this.initializeForm();

    this.form.get('countryCode')!.valueChanges.subscribe(countryCode => {
      this.checkIfCurrencyAvailableForCountry(countryCode);
      this.toggleVatFieldDependingOnCountry(countryCode, countryCode); // countryCode is passed 2nd time as placeholder
      this.toggleStateCode(countryCode);
    });

    this.toggleVatFieldDependingOnCountry(this.formValue.countryCode, this.formValue.vatNumber);
    this.toggleStateCode(this.formValue.countryCode, this.formValue.stateCode);

    this.form.markAsPristine(); // because we might have changed form value due to Vat field being added
    this.form.valueChanges.subscribe(this.valueChanges);
    this.form.statusChanges.subscribe(this.statusChanges);
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      countryCode: this.fb.control(this.formValue.countryCode, [
        noWhitespaceValidator,
        Validators.required,
      ]),
      companyName: this.fb.control(this.formValue.companyName, [
        noWhitespaceValidator,
        Validators.required,
      ]),
      address: this.fb.control(this.formValue.address, [
        noWhitespaceValidator,
        Validators.required,
      ]),
      city: this.fb.control(this.formValue.city, [noWhitespaceValidator, Validators.required]),
      postalCode: this.fb.control(this.formValue.postalCode, [
        noWhitespaceValidator,
        Validators.required,
      ]),
    });
  }

  private showVatField(countryCode: string): boolean {
    return showVatField.includes(countryCode);
  }

  private isVatOptional(countryCode: string): boolean {
    if (countryCode === 'GB') return true;
    if (countryCode === 'DE') return this.chargebeeEntity !== 'bv';
    if (countryCode === 'FR') return this.tellentSasEnabled ? this.chargebeeEntity !== 'bv' : false;
    return false;
  }

  private hasStateCodeRequired(countryCode: string): boolean {
    return countriesWithState.includes(countryCode);
  }

  private toggleVatFieldDependingOnCountry(countryCode: string, vatNumberOrPlaceholder?: string) {
    if (this.showVatField(countryCode)) {
      const isRequired = !this.isVatOptional(countryCode);
      const value =
        this.form.get('countryCode')!.value === this.formValue.countryCode
          ? this.formValue.vatNumber
          : isRequired
            ? getCorrectVatPlaceholder(vatNumberOrPlaceholder)
            : undefined;

      if (!this.form.contains('vatNumber')) {
        this.form.addControl(
          'vatNumber',
          this.fb.control(value, isRequired ? vatNumberRequiredValidator : undefined),
        );
      } else {
        this.form.get('vatNumber')!.reset(value);
        this.form.get('vatNumber')!.setValidators(isRequired ? vatNumberRequiredValidator : null);
        this.form.get('vatNumber')!.updateValueAndValidity();
      }
    } else {
      this.form.removeControl('vatNumber');
    }
  }

  private toggleStateCodeFieldDependingOnCountry(countryCode: string, stateCode?: string) {
    if (this.hasStateCodeRequired(countryCode)) {
      const value =
        this.form.get('countryCode')!.value === this.formValue.countryCode
          ? this.formValue.stateCode
          : stateCode;

      if (!this.form.contains('stateCode')) {
        this.form.addControl('stateCode', this.fb.control(value, Validators.required));
      } else {
        this.form.get('stateCode')!.reset(value);
      }
    } else {
      this.form.removeControl('stateCode');
    }
    this.cdRef.detectChanges();
  }

  private checkIfCurrencyAvailableForCountry(countryCode: string): void {
    const currencyForCountry = getCountryAvailableCurrencies(countryCode);
    const selectedCountryName = this.countries.find(country => country.value === countryCode)
      ?.option;
    if (
      currencyForCountry &&
      currencyForCountry !== this.selectedCurrency &&
      !this.formValue.countryCode &&
      selectedCountryName
    ) {
      this.openCurrencyChangeConfirmationDialog({ currencyForCountry, selectedCountryName });
    }
  }

  private openCurrencyChangeConfirmationDialog({
    currencyForCountry,
    selectedCountryName,
  }: {
    currencyForCountry: Currency;
    selectedCountryName: string;
  }) {
    this.dialogSvc
      .information({
        title: this.i18n.translate(
          'app.pricing.billing_address_form.currency_change_dialog.title',
          { oldCurrency: this.selectedCurrency, newCurrency: currencyForCountry },
        ),
        message: this.i18n.translate(
          'app.pricing.billing_address_form.currency_change_dialog.message',
          { countryName: selectedCountryName, currency: currencyForCountry },
        ),
        agreeLabel: this.i18n.translate(
          'app.pricing.billing_address_form.currency_change_dialog.agreeLabel',
        ),
      })
      .subscribe(result => {
        if (result) {
          this.currencyChanges.emit(currencyForCountry);
        } else {
          this.form.get('countryCode')!.setValue(null);
        }
      });
  }

  private getRegionsCollection(
    countryCode: string,
  ): Observable<Array<{ option: string; value: any }>> {
    return this.regionsStoreService.getOrLoadCountryRegions(countryCode).pipe(
      map(regions =>
        regions.map(({ name: option, code: value }) => ({
          option,
          value,
        })),
      ),
    );
  }

  private toggleStateCode(countryCode: string, stateCode?: string) {
    this.toggleStateCodeFieldDependingOnCountry(countryCode, stateCode);
    if (this.hasStateCodeRequired(countryCode)) {
      this.getRegionsCollection(countryCode)
        .pipe(first())
        .subscribe(regions => {
          this.regions = regions;
          this.cdRef.detectChanges();
        });
    }
  }
}
